import {useMemo} from 'react'

import {
  useConsultationsMyAppointmentsQuery,
  useConsultationsProgramDetailQuery,
} from '@progyny/generated/graphql'
import {normalizeProgramAppointment} from '@progyny/pages/Consultations/Menopause/utils'
import {
  MENOPAUSE_ONBOARDING_SLUG,
  MENOPAUSE_PROGRAM_SLUG,
} from '@progyny/pages/Consultations/constants'
import type {ConsultationListAppointmentItem} from '@progyny/pages/Consultations/types/consultationListAppointmentItem'
import {
  filterPastAppointments,
  filterUpcomingAppointments,
  formatAppointmentDate,
  formatAppointmentTime,
  getAppointmentsBySlug,
  getConsultationsBySlug,
} from '@progyny/utils/appointmentHelpers'

export {MENOPAUSE_PROGRAM_SLUG} from '@progyny/pages/Consultations/constants'

export type MenopauseAppointmentItem = ConsultationListAppointmentItem

export function useMenopauseConsultationAppointments() {
  const {
    data: appointmentsData,
    loading: appointmentsLoading,
    refetch: refetchAppointments,
  } = useConsultationsMyAppointmentsQuery({fetchPolicy: 'cache-first'})

  const {
    data: programDetailData,
    loading: programDetailLoading,
    refetch: refetchProgramDetail,
  } = useConsultationsProgramDetailQuery({
    variables: {slug: MENOPAUSE_PROGRAM_SLUG},
    fetchPolicy: 'cache-first',
  })

  const loading = appointmentsLoading || programDetailLoading

  const program = programDetailData?.consultations?.program
  const programEnrollment =
    programDetailData?.consultations?.myProgramEnrollments?.find(
      (e) => e?.program?.id === program?.id
    )
  const sessionStatuses = programEnrollment?.sessionStatuses ?? undefined

  return useMemo(() => {
    const programAppointments = getAppointmentsBySlug(
      MENOPAUSE_PROGRAM_SLUG,
      appointmentsData
    )
    const programConsultations = getConsultationsBySlug(
      MENOPAUSE_ONBOARDING_SLUG,
      appointmentsData
    )
    const allMenopauseAppointments = [
      ...programAppointments,
      ...programConsultations,
    ]
    const programUpcoming = filterUpcomingAppointments(allMenopauseAppointments)
    const programPast = filterPastAppointments(allMenopauseAppointments)

    const upcoming: MenopauseAppointmentItem[] = []
    const past: MenopauseAppointmentItem[] = []

    programUpcoming.forEach((apt) => {
      const item = normalizeProgramAppointment(apt, sessionStatuses)
      if (item) upcoming.push(item)
    })
    programPast.forEach((apt) => {
      const item = normalizeProgramAppointment(apt, sessionStatuses)
      if (item) past.push(item)
    })

    upcoming.sort((a, b) => {
      const tA = a.scheduledTime ? new Date(a.scheduledTime).getTime() : 0
      const tB = b.scheduledTime ? new Date(b.scheduledTime).getTime() : 0
      return tA - tB
    })

    past.sort((a, b) => {
      const tA = a.scheduledTime ? new Date(a.scheduledTime).getTime() : 0
      const tB = b.scheduledTime ? new Date(b.scheduledTime).getTime() : 0
      return tB - tA
    })

    return {
      upcoming,
      past,
      hasUpcoming: upcoming.length > 0,
      hasPast: past.length > 0,
      loading,
      formatDate: formatAppointmentDate,
      formatTime: formatAppointmentTime,
      refetchAppointments,
      refetchProgramDetail,
    }
  }, [
    appointmentsData,
    sessionStatuses,
    loading,
    refetchAppointments,
    refetchProgramDetail,
  ])
}
